import { isStandaloneReturnStatus } from "@repo/shared/types";
import { useEffect, useRef, useState } from "react";

/** Seconds the advisor gets to read the reached step before being sent back. */
export const STANDALONE_AUTO_CONTINUE_SECONDS = 5;

type Args = {
  /** Current process status (undefined while loading). */
  status: string | undefined;
  /** Sanitized return target, or null when there is none — disables the hook. */
  returnUrl: string | null;
  /**
   * Whether the countdown may fire on its own. False when the return target
   * only came from the link (not persisted on the instance): then the Continue
   * button still works, but nothing navigates without a click.
   */
  autoReturn?: boolean;
  /**
   * Treat an initial return status as a transition, e.g. when the page was
   * opened right after a standalone submit that may already have advanced.
   */
  armed?: boolean;
  /** Countdown length; exposed for tests/tuning. */
  seconds?: number;
};

export type StandaloneAutoContinue = {
  /** Seconds left before the redirect fires, or null when no countdown is running. */
  secondsLeft: number | null;
  /** True once `window.location.assign` has been called. */
  redirecting: boolean;
  /** Stop the countdown and stay on the page (the Continue button remains). */
  cancel: () => void;
  /** Go now — the Continue button's click handler. */
  continueNow: () => void;
};

/**
 * QL-264 / QL-263 — auto-continue back to the partner system once the advice
 * process reaches a hand-off step (`STANDALONE_RETURN_STATUSES`: the review
 * steps or completed) while the advisor is watching the process page
 * in standalone mode. Those steps run in the background and can take time; the
 * partner reopens the instance when there is more to do.
 *
 * Fires only on a TRANSITION into a return status observed during this mount
 * (or on an initial return status when `armed`), never merely because an
 * instance already resting at one was opened — that would bounce someone who
 * came back to look at the documents. Gives a short countdown so the step is
 * readable and the Continue button stays clickable; `cancel` keeps the user on
 * the page for the rest of this visit.
 */
export function useStandaloneAutoContinue({
  status,
  returnUrl,
  autoReturn = true,
  armed = false,
  seconds = STANDALONE_AUTO_CONTINUE_SECONDS,
}: Args): StandaloneAutoContinue {
  const sawNonReturn = useRef(false);
  const fired = useRef(false);
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  const [redirecting, setRedirecting] = useState(false);
  const [cancelled, setCancelled] = useState(false);

  useEffect(() => {
    if (status && !isStandaloneReturnStatus(status)) sawNonReturn.current = true;
  }, [status]);

  const shouldRun =
    !!returnUrl &&
    autoReturn &&
    !!status &&
    isStandaloneReturnStatus(status) &&
    !cancelled &&
    !fired.current &&
    (sawNonReturn.current || armed);

  useEffect(() => {
    if (!shouldRun) return;
    setSecondsLeft(seconds);
    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      const left = Math.max(0, seconds - Math.floor((Date.now() - startedAt) / 1000));
      setSecondsLeft(left);
      if (left === 0) {
        window.clearInterval(timer);
        if (!fired.current && returnUrl) {
          fired.current = true;
          setRedirecting(true);
          window.location.assign(returnUrl);
        }
      }
    }, 250);
    return () => window.clearInterval(timer);
  }, [shouldRun, seconds, returnUrl]);

  const cancel = () => {
    setCancelled(true);
    setSecondsLeft(null);
  };

  const continueNow = () => {
    if (!returnUrl || fired.current) return;
    fired.current = true;
    setRedirecting(true);
    window.location.assign(returnUrl);
  };

  return { secondsLeft: shouldRun ? secondsLeft : null, redirecting, cancel, continueNow };
}
